import React, {useState} from 'react';
import {useTheme} from "@mui/material/styles";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { DaButton } from "../form/form.styles";
import { MessageBox } from "./MessageBox";
import { IMessagePanelProps, MsgBoxType, IUserAutocompleteDialogProps } from "./types";
import {throttle} from "lodash";
import {ILookupCustomer, ILookupUserSearchTerm, lookupApi} from "../../../_api";
import {useAuth} from "../../../_auth/useAuth";

/**
 * Extracts the email domain from an email.
 * Takes the word after @ and before the first dot.
 * Example:
 *   user@company.com -> returns 'company'
 *   user@noriina.shop -> returns 'noriina'
 *   user@edu.xn--yaho-sqa.com -> returns 'edu'
 */

export const CustomerAddDialog: React.FC<IUserAutocompleteDialogProps> = (
    {
        isOpen = false,
        onClose,
        title = "Choose Authorized Customer",
        userId = -1,
        userEmail = '',
        isBrokerRequested = false
    }: IUserAutocompleteDialogProps
) => {
    const theme = useTheme();
    const { getAccessToken } = useAuth();
    const [selItem, setSelItem] = React.useState<number | undefined>(undefined);
    const [customerOptions, setCustomerOptions] = useState<Array<ILookupCustomer>>([]);
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [msgBoxOpen, setMsgBoxOpen] = React.useState<boolean>(false);
    const [msgBoxProps, setMsgBoxProps] = useState<IMessagePanelProps>({message: ""});

    const formFieldSize = "medium";
    const formFieldVariant = "standard";

    const handleClose = () => {
        onClose("cancel");
        setSelItem(undefined);
        setSearchTerm("");
        setOpen(false);
    };

    const handleOk = () => {
        onClose("ok", selItem);
        setSelItem(undefined);
        setSearchTerm("");
        setOpen(false);
    };

    const msgBox_Close = (value?: string) => {
        setMsgBoxOpen(false);
    }

    const msgBox_Show = (message: string, title?: string, type?: MsgBoxType) => {
        setMsgBoxProps({
            message: message,
            title: title,
            type: type,
        })
        setMsgBoxOpen(true);
    }

    const lookupSearch = React.useRef(
        throttle(async (search_term) => {
            if (search_term.length < 3) {
                setCustomerOptions([]);
                setOpen(false);
            } else {
                const options = await getLookupOptions(search_term) as Array<ILookupCustomer>;
                setCustomerOptions(options);
                setOpen(true);
            }
        }, 500)
    ).current;

    const lookupInputChanged = async (e: React.SyntheticEvent, newValue: string, reason: string): Promise<void> => {
        setSearchTerm(newValue);
        // Don't re-run search for programmatic change, user selection
        if (reason !== "reset") {
            setOpen(true);
            lookupSearch(newValue);
        }
    }

    const getLookupOptions = async (search_term: string): Promise<Array<ILookupCustomer> | void> => {
        const options: ILookupUserSearchTerm = {user_id: userId, search_term: search_term, company_type: isBrokerRequested ? 'LISTADMIN' : 'CUSTOMER'};
        const auth_token = await getAccessToken();
        let ret_val = [];
        try {
            const resp = await lookupApi.get_user_customer_lookup(options, auth_token);
            if (resp) {
                if (resp.success) {
                    ret_val = resp.data;
                } else if (resp.error && resp.error.length > 0) {
                    msgBox_Show(
                        "An error occurred while retrieving Authorized Customer lookups data. " +
                            "The Authorized Customer lookups data was not found.",
                        "Authorized Customer lookups Not Found Error",
                        "error"
                    );
                }
            }
        }
        catch (err) {
            msgBox_Show(
                "An error occurred while fetching Authorized Customer lookups data. " +
                "There may have been a network error.",
                "Lookup API Fetch Error",
                "error"
            );
        }
        return ret_val;
    };

    return (
        <>
                <Dialog
                    open={isOpen}
                    onClose={handleClose}
                    fullWidth={true}
                    maxWidth={"sm"}
                    aria-labelledby="dialog-box-title"
                    aria-describedby="dialog-box-description"
                >
                    <DialogTitle
                        id="dialog-box-title"
                        sx={{
                            display: "flex",
                            alignItems: "end",
                            justifyContent: "start"
                        }}
                    >
                        {title}
                    </DialogTitle>
                    <DialogContent sx={{whiteSpace: "pre-line"}}>
                        <Autocomplete
                            open={open}
                            onOpen={() => setOpen(true)}
                            onClose={() => setOpen(false)}
                            inputValue={searchTerm}
                            filterOptions={(x) => x}
                            size={formFieldSize}
                            fullWidth
                            id=""
                            options={customerOptions.map(({company_id, company_sid, company_name, company_domain}) =>
                                ({value: company_id, label: `${company_sid} - ${company_name} - @${company_domain}`, company_domain})
                            )}
                            getOptionLabel={(option) => option.label}
                            onChange={(e, value) => {
                                let newValue = value?.value ?? undefined;
                                setSelItem(newValue);
                            }}
                            onInputChange={lookupInputChanged}
                            isOptionEqualToValue={(option, value) =>
                                value === undefined || option.value === value.value
                            }
                            noOptionsText={"No options available"}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.value}>
                                    {option.label}
                                </Box>
                            )}
                            renderInput={(params) =>
                                <TextField {...params}
                                    label="Authorized Company"
                                    variant={formFieldVariant}
                                    // error={!!errors[name]}
                                    // helperText={errors[name]?.message ?? ''}
                                />
                            }
                        />
    
                </DialogContent>
                <DialogActions>
                    <DaButton buttonStyle="secondary" variant={"outlined"} autoFocus onClick={handleClose}>Cancel</DaButton>
                    <DaButton buttonStyle="primary" variant={"outlined"} onClick={handleOk}>OK</DaButton>
                </DialogActions>
            </Dialog>
            <MessageBox
                isOpen={msgBoxOpen}
                onClose={msgBox_Close}
                {...msgBoxProps}
            />
        </>
    );
}